// @ts-strict-ignore
import {ToastType} from 'common/components/toast/types';
import {ToggleOption} from 'common/components/toggle/types';
import partnerUserProps from 'shared_configs/data_definitions/partner_user_properties.json';
import reservedUserProps from 'shared_configs/data_definitions/reserved_user_properties.json';
import defaultProps from 'shared_configs/data_definitions/default_properties.json';
import geolocationProps from 'shared_configs/data_definitions/geolocation_properties.json';
import adProperties from 'shared_configs/data_definitions/ad_properties.json';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {Translatable, TranslationKey} from 'common/i18n';
import {
  ChartType,
  ConversionWindowUnit,
  FUNNELS_MATH_TYPES,
  NUMERIC_MATH_TYPES,
  PER_USER_AGGREGATIONS,
  PRIMITIVE_MATH_TYPES,
  PropertyType,
  ValueRepresentationType,
  XAU_MATH_TYPES,
} from 'common/types/reports/bookmark';

import {ResidencyClusterCountryCode} from './types';

export enum ResourceType {
  Event = `event`,
  Formula = `formula`,
  User = `user`,
  Cohort = `cohort`,
  Other = `other`,
  RuntimeProperty = `create_custom_runtime_property`,
  Metric = `metric`,
}

/** List of project ids for internal testing and dogfooding (e.g., api migrations, enabling features) */
export const INTERNAL_PROJECTS = new Set([3, 4, 5, 8, 132990, 1297132, 1722485, 2460016]);

export const DEFAULT_CHART_HEIGHT = 600;
export const DEFAULT_CHART_WIDTH = 800;

// amount to time to show the completed, or failed toast before closing the original downloading toast
export const DOWNLOAD_TOAST_OVERLAP_MS = 1500;

export const DOWNLOAD_SUCCESS_TOAST = {
  type: ToastType.SUCCESS,
  message: `Export Complete`,
};

export const DOWNLOAD_FAIL_TOAST = {
  type: ToastType.ERROR,
  message: `Export Failed`,
};

export const UNIQUE_PROJECTS = {
  ApnaTime: `1993867`,
  CotalityRealEstate: `2175557`,
  CrunchyRoll: `2718323`,
  Getir: `2271745`,
  GoKiwiProd: `2951793`,
  InvestingDotCom: `3190589`,
  JioHotstarProd: `3854992`,
  JioHotstarPreProd: `3854994`,
  OneFootballCdpProd: `2979089`,
  Meesho: `1155510`,
  NykaaBeauty: `2852652`,
  NykaaFashion: `2852656`,
  Picpay: `467873`,
  SuperPlay: `2036368`,
  SuperPlayEu: `2735821`,
  Uber: `2087667`,
  UberEats: `2764429`,
  Viacom: `2928584`,
  Yelp: `2535277`,
  Zalora: `2620273`,
  Zee5: `1903813`,
  Zepto: `2462203`,
};

export const UNIQUE_ORGS = {
  Uber: `1718725`,
};

export const LIMITED_CONCURRENCY_PROJECTS = new Set([
  UNIQUE_PROJECTS.ApnaTime,
  UNIQUE_PROJECTS.Getir,
  UNIQUE_PROJECTS.InvestingDotCom,
  UNIQUE_PROJECTS.JioHotstarProd,
  UNIQUE_PROJECTS.JioHotstarPreProd,
  UNIQUE_PROJECTS.OneFootballCdpProd,
  UNIQUE_PROJECTS.Picpay,
  UNIQUE_PROJECTS.SuperPlay,
  UNIQUE_PROJECTS.SuperPlayEu,
  UNIQUE_PROJECTS.Uber,
  UNIQUE_PROJECTS.Viacom,
  UNIQUE_PROJECTS.Zee5,
]);

export const VERY_LIMITED_CONCURRENCY_PROJECTS = new Set([
  UNIQUE_PROJECTS.CotalityRealEstate,
  UNIQUE_PROJECTS.GoKiwiProd,
  UNIQUE_PROJECTS.Meesho,
  UNIQUE_PROJECTS.NykaaBeauty,
  UNIQUE_PROJECTS.NykaaFashion,
  UNIQUE_PROJECTS.UberEats,
  UNIQUE_PROJECTS.Zepto,
]);

// NOTE: These projects are limited on certain attribution features and CSV export size.
const LARGE_PROJECTS = new Array([
  UNIQUE_PROJECTS.CrunchyRoll,
  UNIQUE_PROJECTS.JioHotstarProd,
  UNIQUE_PROJECTS.JioHotstarPreProd,
  UNIQUE_PROJECTS.Meesho,
  UNIQUE_PROJECTS.Picpay,
  UNIQUE_PROJECTS.Uber,
  UNIQUE_PROJECTS.UberEats,
  UNIQUE_PROJECTS.Viacom,
  UNIQUE_PROJECTS.Yelp,
  UNIQUE_PROJECTS.Zalora,
  UNIQUE_PROJECTS.Zee5,
]);

// Changes/additions to this config should also be reflected in the query-api config `attribution_max_date_range_projects`
// https://github.com/mixpanel/analytics/blob/16b6831b62e328cb68ac364640ba801d6c0992a4/kube/lib/query-api/query-api-config.libsonnet#L114
// todo: move this to a shared configmap instead
export const LIMITED_ATTRIBUTION_PROJECTS = new Set(...LARGE_PROJECTS);
export const CUSTOM_ATTRIBUTION_MAX_RANGE: {[key: string]: number} = {
  ...Object.fromEntries(LARGE_PROJECTS.map((project) => [project, 30])),
  '3024401': 366,
};

export const LIMITED_CSV_EXPORT_PROJECTS = new Set(...LARGE_PROJECTS);

export enum Loading {
  Bookmarks = `bookmarks`,
  BookmarkedReport = `bookmarkedReport`,
  Download = `download`,
  Result = `result`,
}

export enum ChartVizType {
  Bar = `Bar`,
  Line = `Line`,
  Metric = `Metric`,
  Steps = `Steps`,
  Sankey = `Sankey`,
}

export enum PseudoChartVizType {
  Bar = `bar`,
  Flows = `sankey`,
  Funnel = `funnel`,
  StackedColumn = `stacked-column`,
  Column = `column`,
  MultiLine = `multi-line`,
  Line = `line`,
  Metric = `metric`,
  Pie = `pie`,
  StackedLine = `stacked-line`,
  Table = `table`,
}

export const ChartTypeDisplayName: Partial<{[key in ChartType]: string}> = {
  [ChartType.Bar]: `Bar`,
  [ChartType.Line]: `Line`,
  [ChartType.Pie]: `Pie`,
  [ChartType.Metric]: `Metric`,
  [ChartType.StackedBar]: `Stacked Bar`,
  [ChartType.StackedLine]: `Stacked Line`,
  [ChartType.Column]: `Column`,
  [ChartType.StackedColumn]: `Stacked Column`,
  [ChartType.Sankey]: `User Flows`,
  [ChartType.Table]: `Table`,
  [ChartType.FunnelSteps]: `Funnel Steps`,
  [ChartType.FunnelTrend]: `Funnel Trends`,
  [ChartType.FunnelFrequencyLine]: `Funnel Frequency Line`,
  [ChartType.FunnelFrequencyBar]: `Funnel Frequency Bar`,
  [ChartType.FunnelTTCBar]: `Funnel Time To Convert Bar`,
  [ChartType.FunnelTTCLine]: `Funnel Time To Convert Line`,
  [ChartType.FunnelMedianTTC]: `Time to Convert`,
  [ChartType.RetentionCurve]: `Retention Curve`,
  // TODO: remove this after retention-consistency work
  [ChartType.RetentionLine]: `Line`,
  [ChartType.RetentionTrend]: `Retention Trends`,
  [ChartType.RetentionTrendMetric]: `Metric`,
  [ChartType.RetentionTable]: `Retention Curve`,
  [ChartType.Frequency]: `Frequency`,
  [ChartType.FunnelTopPaths]: `Top Paths`,
  [ChartType.FlowsTopPaths]: `Top Paths`,
  [ChartType.Flows]: `User Flows`,
  [ChartType.ImpactAdoption]: `Adoption Over Time`,
  [ChartType.ImpactTrends]: `Impact Trends`,
  [ChartType.ImpactPropensity]: `Causal Impact`,
};

export const ChartTypeDisplayNameKey: Partial<{[key in ChartType]: TranslationKey}> = {
  [ChartType.Bar]: `reports.chart-types.bar`,
  [ChartType.Line]: `reports.chart-types.line`,
  [ChartType.Pie]: `reports.chart-types.pie`,
  [ChartType.Metric]: `reports.chart-types.metric`,
  [ChartType.StackedBar]: `reports.chart-types.stacked-bar`,
  [ChartType.StackedLine]: `reports.chart-types.stacked-line`,
  [ChartType.Column]: `reports.chart-types.column`,
  [ChartType.StackedColumn]: `reports.chart-types.stacked-column`,
  [ChartType.Sankey]: `reports.chart-types.sankey`,
  [ChartType.Table]: `reports.chart-types.table`,
  [ChartType.FunnelSteps]: `reports.chart-types.funnel-steps`,
  [ChartType.FunnelTrend]: `reports.chart-types.funnel-trend`,
  [ChartType.FunnelFrequencyLine]: `reports.chart-types.funnel-frequency-line`,
  [ChartType.FunnelFrequencyBar]: `reports.chart-types.funnel-frequency-bar`,
  [ChartType.FunnelTTCBar]: `reports.chart-types.funnel-ttc-bar`,
  [ChartType.FunnelTTCLine]: `reports.chart-types.funnel-ttc-line`,
  [ChartType.FunnelMedianTTC]: `reports.chart-types.funnel-median-ttc`,
  [ChartType.RetentionCurve]: `reports.chart-types.retention-curve`,
  // TODO: remove this after retention-consistency work
  [ChartType.RetentionLine]: `reports.chart-types.retention-line`,
  [ChartType.RetentionTrend]: `reports.chart-types.retention-trends`,
  [ChartType.RetentionTrendMetric]: `reports.chart-types.metric`,
  [ChartType.RetentionTable]: `reports.chart-types.retention-curve`,
  [ChartType.Frequency]: `reports.chart-types.frequency`,
  [ChartType.FunnelTopPaths]: `reports.chart-types.funnel-top-paths`,
  [ChartType.FlowsTopPaths]: `reports.chart-types.flows-top-paths`,
  [ChartType.Flows]: `reports.chart-types.flows`,
  [ChartType.ImpactAdoption]: `reports.chart-types.impact-adoption`,
  [ChartType.ImpactTrends]: `reports.chart-types.impact-trends`,
  [ChartType.ImpactPropensity]: `reports.chart-types.impact-propensity`,
};

export const ChartTypeTooltip: Partial<{[key in ChartType]: Translatable}> = {
  [ChartType.Bar]: ({t}) => t(`reports.chart-types.tooltips.bar`),
  [ChartType.Line]: ({t}) => t(`reports.chart-types.tooltips.line`),
  [ChartType.Pie]: ({t}) => t(`reports.chart-types.tooltips.pie`),
  [ChartType.Metric]: ({t}) => t(`reports.chart-types.tooltips.metric`),
  [ChartType.StackedBar]: ({t}) => t(`reports.chart-types.tooltips.stacked-bar`),
  [ChartType.StackedLine]: ({t}) => t(`reports.chart-types.tooltips.stacked-line`),
  [ChartType.Column]: ({t}) => t(`reports.chart-types.tooltips.column`),
  [ChartType.StackedColumn]: ({t}) => t(`reports.chart-types.tooltips.stacked-column`),
  [ChartType.Sankey]: ({t}) => t(`reports.chart-types.tooltips.sankey`),
  [ChartType.Table]: ({t}) => t(`reports.chart-types.tooltips.table`),
  [ChartType.FunnelSteps]: ({t}) => t(`reports.chart-types.tooltips.funnel-steps`),
  [ChartType.FunnelTrend]: ({t}) => t(`reports.chart-types.tooltips.funnel-trend`),
  [ChartType.FunnelFrequencyLine]: ({t}) => t(`reports.chart-types.tooltips.funnel-frequency-line`),
  [ChartType.FunnelFrequencyBar]: ({t}) => t(`reports.chart-types.tooltips.funnel-frequency-bar`),
  [ChartType.FunnelTTCBar]: ({t}) => t(`reports.chart-types.tooltips.funnel-ttc-bar`),
  [ChartType.FunnelTTCLine]: ({t}) => t(`reports.chart-types.tooltips.funnel-ttc-line`),
  [ChartType.FunnelMedianTTC]: ({t}) => t(`reports.chart-types.tooltips.funnel-median-ttc`),
  [ChartType.RetentionCurve]: ({t}) => t(`reports.chart-types.tooltips.retention-curve`),
  // TODO: remove this after retention-consistency work
  [ChartType.RetentionLine]: ({t}) => t(`reports.chart-types.tooltips.retention-line`),
  [ChartType.RetentionTrend]: ({t}) => t(`reports.chart-types.tooltips.retention-trends`),
  [ChartType.RetentionTable]: ({t}) => t(`reports.chart-types.tooltips.retention-curve`),
  [ChartType.Frequency]: ({t}) => t(`reports.chart-types.tooltips.frequency`),
  [ChartType.FunnelTopPaths]: ({t}) => t(`reports.chart-types.tooltips.funnel-top-paths`),
  [ChartType.FlowsTopPaths]: ({t}) => t(`reports.chart-types.tooltips.flows-top-paths`),
  [ChartType.Flows]: ({t}) => t(`reports.chart-types.tooltips.flows`),
  [ChartType.ImpactAdoption]: ({t}) => t(`reports.chart-types.tooltips.impact-adoption`),
  [ChartType.ImpactTrends]: ({t}) => t(`reports.chart-types.tooltips.impact-trends`),
  [ChartType.ImpactPropensity]: ({t}) => t(`reports.chart-types.tooltips.impact-propensity`),
};

export const ValueRepresentationTypeSelection: {[key in ValueRepresentationType]: ToggleOption} = {
  [ValueRepresentationType.Absolute]: {
    icon: `value-absolute`,
    label: ``,
    value: ValueRepresentationType.Absolute,
    tooltipLabel: `Absolute counts total amounts.`,
  },
  [ValueRepresentationType.Relative]: {
    icon: `value-relative`,
    label: ``,
    value: ValueRepresentationType.Relative,
    tooltipLabel: `Relative calculates the percentage of the whole.`,
  },
};

export const SESSION_START_EVENT_NAME = `$session_start`;
export const SESSION_END_EVENT_NAME = `$session_end`;
export const SESSION_EVENT_NAMES = Object.freeze({
  start: SESSION_START_EVENT_NAME,
  end: SESSION_END_EVENT_NAME,
});
export const SESSION_EVENTS = Object.freeze({
  [SESSION_START_EVENT_NAME]: {session_event: `start`},
  [SESSION_END_EVENT_NAME]: {session_event: `end`},
});

export const SPECIAL_EVENTS = {
  ALL_EVENTS: `$all_events`,
  ALL_MTU_EVENTS: `$all_mtu_events`,
  SELECTED_STEP: `$selected_step`,
  TOP_EVENTS: `$top_events`,
  ANY_EVENT: `$mp_anything_event`,
  SESSION_START: SESSION_START_EVENT_NAME,
  SESSION_END: SESSION_END_EVENT_NAME,
};

export const SPECIAL_ALL_EVENT_VALUES = Object.freeze([
  SPECIAL_EVENTS.ALL_EVENTS,
  SPECIAL_EVENTS.ANY_EVENT,
  SPECIAL_EVENTS.SELECTED_STEP,
  SPECIAL_EVENTS.TOP_EVENTS,
  SPECIAL_EVENTS.ALL_MTU_EVENTS,
]);
export const SPECIAL_EVENT_VALUES = Object.freeze(Object.values(SPECIAL_EVENTS));

/* eslint-disable camelcase */
export const EVENTS = {
  [SPECIAL_EVENTS.ALL_EVENTS]: `All Events`,
  [SPECIAL_EVENTS.ANY_EVENT]: `Any event`, // Keep Lowercase e in event to be consistent with current implementation, for now
  [SPECIAL_EVENTS.SELECTED_STEP]: `Selected Step`,
  [SPECIAL_EVENTS.TOP_EVENTS]: `Your Top Events`,
  [SPECIAL_EVENTS.SESSION_END]: `Session End`,
  [SPECIAL_EVENTS.SESSION_START]: `Session Start`,
  $ad_spend: `Ad Spend`,
  $ae_crashed: `App Crashed`,
  $ae_first_open: `First App Open`,
  $ae_iap: `In-App Purchase`,
  $ae_notif_opened: `Push Notification Opened`,
  $ae_session: `App Session`,
  $ae_updated: `App Updated`,
  $campaign_bounced: `Message Bounced`,
  $campaign_delivery: `Message Sent`,
  $campaign_marked_spam: `Message Marked Spam`,
  $campaign_link_click: `Message Link Clicked`,
  $campaign_open: `Message Opened`,
  $campaign_received: `Message Received`,
  $create_alias: `Create Alias`,
  $experiment_started: `Experiment Started`,
  $identify: `Identify`,
  $journey_entered: `Campaign Entered`,
  $message_suppressed: `Message Suppressed`,
  $mp_click: `[Auto] Element Click`,
  $mp_input_change: `[Auto] Input Change`,
  $mp_scroll: `[Auto] Page Scroll`,
  $mp_session_record: `Session Recording Checkpoint`,
  $mp_submit: `[Auto] Form Submit`,
  $mp_web_page_view: `[Auto] Page View`,
  $show_survey: `Show Survey`,
  $signup: `Signup`,
};
/* eslint-enable camelcase */

const RESERVED_PROPERTY_DEFINITIONS = [
  ...partnerUserProps,
  ...reservedUserProps,
  ...defaultProps,
  ...geolocationProps,
  ...adProperties,
];

export const RESERVED_PROPS_TO_DISPLAY_NAME = RESERVED_PROPERTY_DEFINITIONS.reduce((acc, prop) => {
  acc[prop[`name`]] = prop[`displayName`] ?? prop[`name`];
  return acc;
}, {});

export const RESERVED_PROPS_TO_DEFINITION = RESERVED_PROPERTY_DEFINITIONS.reduce((acc, prop) => {
  acc[prop[`name`]] = prop;
  return acc;
}, {});

export const DATASET_PROPERTY_NAME = `$dataset`;

export const HISTORICAL_PROFILE_PROPERTY_PREFIX = `$mp_profile:`;
export const SCD_PROFILE_PROPERTY_PREFIX = `$scd:`;
export const EXPERIMENT_PROPERTY_PREFIX = `$experiment:`;
export const EXPERIMENT_TYPE_KEY = `$experiment_type`;
export const EXPERIMENT_TYPE_FEATURE_FLAG = `feature_flag`;
export const FUNNEL_CONVERSION_TIME_VALUE = `$time_to_conversion`;

export enum ProjectType {
  Normal = 1,
  Rollup = 3,
}

export const DATASET_MIXPANEL = `$mixpanel`;
export const DATASET_SALESFORCE = `salesforce`;
export const DATASET_ZENDESK = `zendesk`;

export const DATASET_SALESFORCE_CONTACTS = `contacts`;
export const DATASET_SALESFORCE_LEADS = `leads`;

export {PropertyType};

export type FullPropertyType = {
  type: PropertyType;
  isList: boolean;
};

export enum ExtendedPropertyType {
  Object = `object`,
  NonProperty = `other`,
  Unknown = `unknown`,
}

export const TYPECAST_PROPERTY_TYPES: PropertyType[] = [
  PropertyType.String,
  PropertyType.Number,
  PropertyType.Boolean,
  PropertyType.Datetime,
];

export const TYPECAST_LIST_PROPERTY_SUBTYPES: PropertyType[] = [...TYPECAST_PROPERTY_TYPES, PropertyType.Object];

export const NUMERIC_PROPERTY_TYPES = [PropertyType.Boolean, PropertyType.Datetime, PropertyType.Number];

export const STRING_TYPECAST_PROPERTIES = [`$experiment_id`, `$variant_id`];

export enum MappedPropertyValues {
  Campaigns = `$campaigns`,
  Dataset = `$dataset`,
  ExperimentId = `$experiment_id`,
  VariantId = `$variant_id`,
}

export enum CountUsersOnceState {
  Enabled = `enabled`,
  Disabled = `disabled`,
  Required = `required`,
}

export const FUNNELS_CONVERSION_RATES = [
  FUNNELS_MATH_TYPES.CONVERSION_RATE_SESSION,
  FUNNELS_MATH_TYPES.CONVERSION_RATE_TOTAL,
  FUNNELS_MATH_TYPES.CONVERSION_RATE_UNIQUE,
];

export const EVENT_MATH_LABELS = {
  [PRIMITIVE_MATH_TYPES.TOTAL]: `Total`,
  [PRIMITIVE_MATH_TYPES.UNIQUE]: `Unique`,
  [PRIMITIVE_MATH_TYPES.SESSIONS]: `Sessions`,
  [PRIMITIVE_MATH_TYPES.AVERAGE]: `Average`,
  [PRIMITIVE_MATH_TYPES.MEDIAN]: `Median`,
  [PRIMITIVE_MATH_TYPES.P25]: `P25`,
  [PRIMITIVE_MATH_TYPES.P75]: `P75`,
  [PRIMITIVE_MATH_TYPES.P90]: `P90`,
  [PRIMITIVE_MATH_TYPES.P99]: `P99`,
  [PRIMITIVE_MATH_TYPES.MIN]: `Min`,
  [PRIMITIVE_MATH_TYPES.MAX]: `Max`,
};

export const PROPERTY_MATH_LABELS = {
  ...EVENT_MATH_LABELS,
  TOTAL: `Sum`,
};

export const XAU_ONLY_MATH_TYPES = Object.values(XAU_MATH_TYPES);

export const ALL_MATH_TYPES = {
  ...PRIMITIVE_MATH_TYPES,
  ...NUMERIC_MATH_TYPES,
  ...XAU_MATH_TYPES,
  ...PER_USER_AGGREGATIONS,
};

export const SPECIAL_SERIES_NAMES = {
  $cohort: `Cohort`,
  $event: `Event`,
  $people: `People`,
};

export const FEATURE_GATES_UNLIMITED = 9223372036854776000; // python max int, aka featureGates.unlimited
export const MAX_DATA_HISTORY_DAYS_UPGRADE_LIMIT = 365 * 5; // maximum upgradeable historic data days

export const RESOURCE_LABELS = Object.freeze({
  USER: `User`,
  GROUP: `Group`,
  EVENT: `Event`,
});

export enum RESIDENCY_CLUSTER_IDS {
  MIXPANEL_PROD_1_ID = 1,
  MIXPANEL_PROD_EU_ID = 5,
  MIXPANEL_PROD_IN_ID = 6,
}

export const RESIDENCY_CLUSTER_IDS_BY_COUNTRY_CODE: {[Key in ResidencyClusterCountryCode]: number} = {
  US: RESIDENCY_CLUSTER_IDS.MIXPANEL_PROD_1_ID,
  EU: RESIDENCY_CLUSTER_IDS.MIXPANEL_PROD_EU_ID,
  IN: RESIDENCY_CLUSTER_IDS.MIXPANEL_PROD_IN_ID,
};

export enum PRODUCTION_DOMAINS {
  US = `mixpanel.com`,
  EU = `eu.mixpanel.com`,
  IN = `in.mixpanel.com`,
}

export const DISABLED_DUE_TO_CLASSIFIED_DATA = `Disabled due to Classified Data`;
// TODO: fill in the doc
export const SCD_COLUMN_SELECTION_LIMIT = 5;

export const SCD_PROPERTY_DISABLED_REASON = {
  DISABLED_DUE_TO_SCD_LIMIT: `You have selected the maximum number of profile history properties supported. [Learn More](https://docs.mixpanel.com)`,
  DISABLED_SCD_IN_DATA_VIEW_FILTERS: `Historical properties are not supported for data view filters`,
  DISABLED_DUE_TO_MULTIPLE_DATA_GROUPS: `Historical properties are not supported when using multiple data groups`,
  getDisabledDueToPropertyType: (propertyType: PropertyType) => {
    return `Historical properties of type '${propertyType}' are not supported for filters or breakdowns`;
  },
  getDisabledDueToMismatchingDataGroup: (dataGroupName: string) => {
    return `Only historical properties from the data group '${dataGroupName}' can be selected`;
  },
};

export const ERROR_DUE_TO_MISSING_PROEPRTY_MAPPING = `Unable to Display Data`;

export enum DownloadMode {
  None = 0,
  File = 1,
  Slack = 2,
}

/**
 * These correspond to ReportNames in reports/constants.py
 */
export enum ReportName {
  AdminOrgSettings = `admin-org-settings`,
  Canvas = `metric-tree`,
  Cohorts = `cohorts`,
  Dashboards = `dashboards`,
  DataAudit = `audit`,
  EntityManagement = `entity-management`,
  Events = `events`,
  EventsSidebar = `events-sidebar`,
  ExperimentReporting = `experiment-reporting`,
  Experiments = `experiments`,
  Explore = `explore`,
  FeatureFlags = `feature-flags`,
  Flows = `flows`,
  Funnels = `funnels`,
  HeatMaps = `heatmaps`,
  HeatMapCollections = `heatmap-collections`,
  Home = `home`,
  Impact = `impact`,
  Insights = `insights`,
  Integrations = `integrations`,
  JqlConsole = `jql-console`,
  Lexicon = `lexicon`,
  Profile = `profile`,
  Retention = `retention`,
  Settings = `settings`,
  Setup = `setup`,
  Signal = `signal`,
  Transformations = `transformations`,
  SessionReplay = `session-replay`,
  UserSettings = `user_settings`,
  Users = `users`,
  Views = `views`,
  Workspaces = `workspaces`,
  Playlists = `playlists`,
}

/**
 * These reports are of legacy status and are in a semi-deprecated/limited support state.
 * This helper is convenient for new features and projects choosing to opt out of supporting
 * legacy reports.
 */
export const LegacyReports: ReportName[] = [
  ReportName.EntityManagement,
  ReportName.ExperimentReporting,
  ReportName.Impact,
  ReportName.Integrations,
  ReportName.JqlConsole,
  ReportName.Signal,
  ReportName.Transformations,
];

/**
 * Restricted report names slightly differ from ReportName which is based on url part.
 * These correspond to FEATURE_GATES['restricted_reports'] usage in configurations/settings_global.py
 * e.g `jql` instead of `jql-console`
 */
export enum RestrictedReport {
  ABTesting = `ab_testing`,
  Campaign = `campaign`,
  Cohorts = `cohorts`,
  ExperimentReporting = `experiment-reporting`,
  Flows = `flows`,
  Formulas = `formulas`,
  Impact = `impact`,
  JqlConsole = `jql`,
  Messages = `messages`,
  Predict = `predict`,
  Revenue = `revenue`,
  Signal = `signal`,
  VisualJourneys = `visual_journeys`,
}

/*
 * These correspond to FEATURE_GATES['restrict_entity_saving'] usage in configurations/settings_global.py
 */
export enum RestrictEntitySaving {
  BehaviorMetrics = `behavior_metrics`,
  Cohorts = `cohorts`,
  CustomProperties = `custom_properties`,
  Formulas = `formulas`,
  LookupTables = `lookup_tables`,
}

/**
 * These correspond to ProjectFeatureFlag in project/models.py
 * and values from OrganizationFeatureflag in webapp/organization/models.py
 */
export enum ProjectFeatureFlag {
  AdvDelEventCountBypass = `del_event_count_bypass`,
  AdvDelRequestPerMonthBypass = `del_per_month_bypass`,
  AdvancedDropFilters = `advanced_drop_filters`,
  AnomalyDetection = `anomaly_detection`,
  AnomalyDetectionViz = `anomaly_detection_viz`,
  AutocaptureInDocs = `autocapture_in_docs`,
  AutoConsistentColors = `auto_consistent_colors`,
  B2B = `b2b_analytics`,
  B2BAnalytics = `b2b_analytics`,
  B2BCohorts = `b2b_cohorts`,
  B2BProject = `b2b_project`,
  BehavioralProperties = `behavioral_properties`,
  BoardBreakdowns = `board_breakdowns`,
  BonferroniCorrection = `bonferroni-correction`,
  Canvas = `canvas`,
  ChangelogModal = `in_product_change_log`,
  ChartCustomization = `chart_customization`,
  // Canonically CompactDatePicker is a FeatureFlag, it is duplicated here to allow for this testing logic: https://github.com/mixpanel/analytics/blob/master/iron/common/report/util/feature-sampling.ts#L447
  CompactDatePicker = `compact_date_picker`,
  Cohorts = `cohorts`,
  ConsistentColors = `consistent_colors`,
  CustomAlertsRCA = `custom_alerts_rca`,
  CustomizeGroups = `customize_groups`,
  CustomProperty = `custom_property`,
  CupedSupport = `cuped-support`,
  DashboardGrid = `dashboards_grid`,
  DashboardManagement = `dashboard_management`,
  DataAuditEventsOnly = `data_audit_events_only`,
  DatabricksJsonPipeline = `databricks_json_pipeline`,
  DataClassification = `data_classification`,
  DataManagement = `data_management`,
  DataTableVirtualized = `data_table_virtualized`,
  DataVolumeMonitoring = `data_volume_monitoring`,
  DataVolumeMonitoringV2 = `data_volume_monitor_v2`,
  Debug = `debug`,
  DefaultGroupedSort = `default_grouped_sort`,
  Dev = `dev`,
  DisableAllEvents = `disable_all_events`,
  DimensionPropertyLookupTable = `dim_prop_lookup_table`,
  DisableEntityManagementCohorts = `disable_em_cohorts`,
  DisableHome = `disable_home`,
  DisableTrends = `disable_trends`,
  DropdownsDataDefs = `dropdowns_data_defs`,
  DropdownsSortedUnique = `dropdowns_sorted_unique`,
  ECommerceTemplate = `e_commerce_template`, // org
  EnableCustomRoles = `enable_custom_roles`,
  EntityManagementCohorts = `entity_mgmnt_cohorts`,
  ERFImprovements = `erf_improvements`,
  EventOwner = `event_owner`,
  EventApproval = `event_approval`,
  EventsCategorization = `events_categorization`,
  EventsMenuRefresh = `events_menu_refresh`,
  EventContext = `event_context`,
  EventsSidebar = `events_sidebar`,
  ExcludeDate = `exclude_date`,
  ExperimentsBreakdown = `experiments_breakdown`,
  ExperimentsHealth = `experiments_health`,
  ExperimentMetricHoverCards = `exp_metric_hover_cards`,
  ExploreNewPage = `explore_new_page`,
  ExtendedERFAccess = `extended_erf_access`,
  FastEdit = `fast_edit`,
  FastEditDev = `fast_edit_dev`,
  FastIterationWorkflow = `fast_iteration_workflow`,
  FlowsAlignmentToggle = `flows_alignment_toggle`,
  FlowsHideCustomEvents = `flows_hide_custom_events`,
  FlowsLessDefaultSteps = `flows_less_steps`,
  FlowsShowCustomEvents = `flows_show_custom_events`,
  FlowsTimePercentiles = `flows_time_percentiles`,
  FoundationalTemplates = `foundational_templates`, // org
  FreeSupportTrial = `free_support_trial`,
  FunnelsChartToggle = `funnels_chart_toggle`,
  FunnelsFrequencyChart = `funnels_frequency_chart`,
  FunnelsFrequencyConversionRate = `funnels_freq_conv_rate`,
  FunnelsNoQueryBuilder = `no_funnels_query_builder`,
  FunnelsQueryBuilder = `funnels_query_builder`,
  FunnelsSessions = `funnels_sessions`,
  FunnelsTrendTypeView = `funnels_trend_type_view`,
  FunnelsViewAsFlows = `funnels_view_flows`,
  GlobalSidenav = `global_sidenav`,
  Goal = `goal`,
  GsNewSearchSplit = `gs_new_search_split`,
  HideExperimentConfidence = `hide_experiment_confidence`,
  HideExportEventsAndUsers = `hide_export_events_users`,
  HideMenuApps = `hide_menu_apps`,
  HideMenuMessages = `hide_menu_messages`,
  HideReportDataAudit = `hide_data_audit`,
  HideReportJqlConsole = `hide_report_jql_console`,
  HideReportRevenue = `hide_report_revenue`,
  HideReportSegmentation = `hide_report_segmentation`,
  HideWeekends = `hide_weekends`,
  HistoricalProfileProps = `historical_profile_props`,
  IdManagementSimplifiedMode = `id_mgmt_simplified_mode`,
  IdManagementUIUpdates = `id_management_ui_updates`,
  InProductPlanBuilder = `in_product_plan_builder`,
  InlineCohorts = `inline_cohorts`,
  InsightsMetricOverrideDefaultSelected = `insight_met_override_sel`,
  InsightsTableViewN = `insights_table_view_n`,
  InspectorApp = `inspector_app`,
  IntegrationsDataViews = `integrations_dataviews`,
  JHSAdvancedDrop = `jhs_advanced_drop`,
  LexiconDropMixpanel = `lexicon_drop_mixpanel`,
  LexiconSidePanel = `lexicon_side_panel`,
  LexiconIssuesHub = `lexicon_issues_hub`,
  LexiconIssuesHubSidePanel = `lexicon_issues_sidepanel`,
  Lift = `lift`, // org
  LiftV1 = `lift_v1`, // org
  LiftV2 = `lift_v2`, // org
  ListInclusionFilters = `list_inclusion_filters`,
  LiveviewEventInspector = `liveview_event_inspector`,
  LiveviewNewUI = `liveview_new_ui`,
  LookupTableSharing = `lookup_table_sharing`,
  MarketingTemplate = `marketing_template`, // org
  MessagesAndExperiments = `messages_and_experiments`,
  MetricLevelDataGroups = `metric_level_data_groups`,
  MetricTreeAI = `metric_tree_ai`, // org
  NavRedesignV1 = `nav_redesign_v1`, // project
  NestedDashboards = `nested_dashboards`,
  NewDatePicker = `new_date_picker`,
  NewDatePresets = `new_date_presets`,
  NewDropdowns = `new_dropdowns`,
  NewPropertyDetection = `new_property_detection`,
  NewTextEditor = `new_text_editor`,
  NoQBCohorts = `no_qb_cohorts`,
  NotifsCampaigns = `notifs_campaigns`,
  NotifsVisualJourneys = `notifs_visual_journeys`,
  NullPropertyMonitoring = `null_property_monitoring`,
  PropertyTypeMonitoring = `property_type_monitoring`,
  Omnisearch = `omnisearch`,
  OnboardingWithCalendly = `onboarding_with_calendly`,
  OnboardingQuestionnaire = `onboarding_questionnaire`,
  OverrideAllEventsDate = `override_all_events_date`,
  PinnedDashboards = `pinned_dashboards`,
  Presence = `presence`,
  PostgresConnector = `postgres_connector`,
  PublicDashboards = `public_dashboards`,
  PipelinesBackfillAllowlist = `backfill_allowlist`,
  SamplingInfo = `sampling_info`,
  QBCohorts = `qb_cohorts`,
  QBMetadataSearch = `qb_metadata_search`,
  QBNewFilters = `qb_new_filters`,
  QBNewMetricsMode = `qb_new_metrics_mode`,
  QBPerLineProperties = `qb_per_line_properties`,
  QBSidebar = `qb_sidebar`,
  QTS = `query_time_sampling`,
  QTSByDefault = `qts_by_default`,
  QueryTimezone = `query_timezone`,
  ReportConsistency = `report_consistency`,
  RetentionChartView = `retention_chart_view`,
  Revenue = `revenue`,
  ScrollMaps = `scroll_maps`,
  ServiceAccountsOptOut = `service_accounts_opt_out`,
  SessionRecording = `session_recording`,
  SessionRecordingAiSummary = `sr_ai_summary`,
  SessionRecordingNetwork = `sr_network`,
  SessionRecordingDebugIndicators = `sr_debug_indicators`,
  // Canonically SessionRecordingMultiTimeline is a FeatureFlag, it is duplicated here to allow for legacy override capability
  SessionRecordingMultiTimeline = `sr_multi_timeline`,
  SessionRecordingSensitiveData = `sr_sensitive_data`,
  SessionRecordingServerSideStitching = `sr_server_side_stitching`,
  SessionRecordingPlaylists = `sr_playlists`,
  ConcurrentReplays = `sr_concurrent_replays`, // org + project
  ShowLast1Day = `show_last_1_day`,
  ShowLast7Days = `show_last_7_days`,
  SparkCopilot = `spark_copilot`,
  FunnelsMathSessions = `funnels_math_sessions`,
  FunnelsConversionSessions = `funnels_conv_sessions`,
  FlowsMathSessions = `flows_math_sessions`,
  ShowCohortsInLexicon = `show_cohorts_in_lexicon`,
  ShowUberWelcome = `show_uber_welcome`,
  ShowUberWelcomeProd = `show_uber_welcome_prod`,
  SingleSegment = `single_segment`,
  SkipFeBm = `skip_fe_bm`,
  SlackDigests = `slack_digests`,
  SlackV2 = `slack_v2`,
  SlowlyChangingDimensionsPoc = `scd_poc`,
  // Shared SSO; This cannot really work as a Project or Org FF in AppDB. Including as a mechanism for modheader
  SSOEnterprise = `sso_enterprise`,
  SSOAdminOrg = `sso_admin_org`,
  /**This Feature Flag changes the behavior of "Require Single Sign-On" so that even Admins and Owners must use SSO*/
  SSORequiredForAll = `sso_required_for_all`,
  Sst = `sst`,
  StarterBoard = `starter_board`,
  TextAnalysis = `text_analysis`,
  TextCards = `text_cards`,
  TrendDataTable = `trend_data_table`,
  // Canonically UnifiedDatePicker is a FeatureFlag, it is duplicated here to allow for this testing logic: https://github.com/mixpanel/analytics/blob/master/iron/common/report/util/feature-sampling.ts#L447
  UnifiedDatePicker = `unified_date_picker`,
  ValueComparison = `value_comparison`,
  ValueComparisonLegacy = `value_comparison_legacy`,
  VerifiedContent = `verified_content`,
  ViberIngestionSampling = `viber_ingestion_sampling`,
  ViewNEverywhere = `view_n_everywhere`,
  ViewNInBarChart = `view_n_in_bar_chart`,
  WarehouseLaunchTemplates = `wh_launch_templates`,
  WarehouseIncreasedPropLimit = `wh_higher_prop_limit`,
  WarehouseForceAddonFeature = `wh_force_addon_feature`,
  WarehouseSpecificScheduling = `wh_specific_scheduling`,
  WarehouseChecksumColumn = `wh_checksum_column`,
  WarehouseTestMode = `wh_test_mode`,
  WarehouseMonitoring = `wh_monitoring`,
  WarehouseDatabricksJobs = `wh_databricks_jobs`,
  WarehouseMirrorInsertId = `wh_mirror_insert_id`,
  DataGovernanceToLexiconTab = `data_gov_to_lexicon`,
  DisableVizOnNonCompliant = `disable_viz_non_comp`,
  CompanyProfile = `company_profile`,
  FirstClassMetrics = `first_class_metrics`,
  OptimizedFunnelSelection = `optimized_funnels`,
  FeatureFlagsApp = `feature_flags_app`,
  FeatureFlagsCohorts = `feature_flags_cohorts`,
  FeatureFlagsGroupTesters = `feature_flag_group_test`,
  FeatureFlagsRemoteConfig = `feature_flags_remote_cfg`,
  UserProfileExperimentExposures = `user_profile_exposures`,
  ExperimentationExposureTable = `exp_exposure_table`,
  FeatureFlagsHistory = `feature_flags_history`,
  FeatureFlagsTestUsers = `feature_flags_test_users`,
  DataStandards = `data_standards`,
  LimitProjectShareByRole = `limit_project_share`,
  OnwersImprovements = `owners_improvement_fe`,
  GroupMigration = `groups_migration`,
  PersistColumnWidths = `persist_column_widths`,
  FiveMinuteAlerts = `5_min_alerts`,
  SendbirdHelpChat = `sendbird_help_chat`,
  DisableSendbirdChat = `disable_sendbird_chat`,
  WebhookAlerts = `webhook_alerts`,
  Winsorization = `winsorization`,
  AnnotationsTimeline = `annotations_timeline`,
  SessionRecordingRemoteSetting = `session-replay-remote-sdk-settings`,
  BoardsMadeEasy = `boards_made_easy`,
  RetentionAwareness = `retention_awareness`,
  ReportComments = `report_comments`,
  FeatureFlagAnnotations = `feature_flag_annotations`,
  ImproveRetentionAwareness = `improve_retention_awareness`,
  SelfServeAuditLogs = `self_serve_audit_logs`,
}

/**
 * These correspond to Feature Flags defined in the Mixpanel web platform
 */
export enum FeatureFlag {
  ConcurrentReplays = `sr_concurrent_replays`,
  DatabricksJsonPipeline = `databricks_json_pipeline`,
  ListInclusionFilters = `list_inclusion_filters`,
  NavigationCoreReportUsage = `navigation-core-report-usage`,
  PostgresConnector = `postgres_connector`,
  SessionRecordingMultiTimeline = `sr_multi_timeline`,
  SessionRecordingNetwork = `session_recording_network`,
  UnifiedDatePicker = `unified_date_picker`,
  ViewNInBarChart = `view-n-in-bar-chart`,
  AnnotationsTimeline = `annotations-timeline`,
  RuntimeEvents = `ff-runtime-events`,
  SessionRecordingPlaylists = `sr_playlists`,
  SessionRecordingRemoteSetting = `session-replay-remote-sdk-settings`,
  BoardsMadeEasy = `boards_made_easy`,
  HideWeekends = `hide_weekends`,
  ReportComments = `report_comments`,
}

/**
 * Defines a list of feature flags to include in tracking
 *
 * @todo [pablo] this is not scalable, do not rely on this
 */
export const TRACKING_FEATURE_FLAGS: ProjectFeatureFlag[] = [
  ProjectFeatureFlag.Cohorts,
  ProjectFeatureFlag.Revenue,
  ProjectFeatureFlag.SlowlyChangingDimensionsPoc,
  ProjectFeatureFlag.ViewNEverywhere,
];

export const SHOW_LAST_X_DAYS_FEATURE_FLAGS: ProjectFeatureFlag[] = [
  ProjectFeatureFlag.ShowLast1Day,
  ProjectFeatureFlag.ShowLast7Days,
];

export const SHOW_LAST_X_DAYS_VALUES: Partial<Record<ProjectFeatureFlag, number>> = {
  [ProjectFeatureFlag.ShowLast1Day]: 1,
  [ProjectFeatureFlag.ShowLast7Days]: 7,
};

export const SHOW_LAST_X_DAYS_UNIT: Partial<Record<ProjectFeatureFlag, TimeUnit>> = {
  [ProjectFeatureFlag.ShowLast1Day]: TimeUnit.Hour,
  [ProjectFeatureFlag.ShowLast7Days]: TimeUnit.Day,
};

export const DEFAULT_FLOWS_MATH = {
  [ProjectFeatureFlag.FlowsMathSessions]: FUNNELS_MATH_TYPES.SESSION,
};

/**
 * These correspond to Whitelist in reports/models.py
 */
export enum UserFeatureFlag {
  Dev = `dev`,
  RollupProjects = `rollup-projects`,
  Sampling = `sampling`,
  SingleSegment = `single_segment`,
}

/**
 * These correspond to UserFlags.FLAGS in user/models.py
 */
export enum UserFlag {
  ClosedPlatformLearnLink = `CLOSED_PLATFORM_LEARN_LINK`,
  DashboardAddedReportOrSeenTooltip = `DASHBOARD_ADDED_REPORT_OR_SEEN_TOOLTIP`,
  MobileInappCustomIntro = `MOBILE_INAPP_CUSTOM_INTRO`,
  RecurringMessagesModal = `RECURRING_MESSAGES_MODAL`,
  ViewedInsightsCount = `VIEWED_INSIGHTS_COUNT`,
  ViewedQueryConsoleIntro = `VIEWED_QUERY_CONSOLE_INTRO`,
  ViewedRecurBetaModal = `VIEWED_RECUR_BETA_MODAL`,
  ViewedRecurV2BetaModal = `VIEWED_RECUR_V2_BETA_MODAL`,
  ViewedReportCount = `VIEWED_REPORT_COUNT`,
  ViewedFYTModal = `VIEWED_BOARDS_FYT_MODAL`,
  ViewedStartupCreditPromo = `VIEWED_STARTUP_CREDIT_PROMO`,
  ViewedRebrandModal = `VIEWED_REBRAND_MODAL`,

  JourneyTut = `JOURNEY_TUT_DONE`,
  MoveIndivCampaign = `VIEWED_MOVE_INDIV_CAMPAIGN_TOOLTIP`,
  MoveJourneyCampaign = `VIEWED_MOVE_JOURNEY_CAMPAIGN_TOOLTIP`,
  NotificationsTut = `NOTIFICATIONS_3_TUT_DONE`,
  OnboardingModal = `VIEWED_ONBOARDING_MODAL`,
  SMSPricingModal = `SMS_PRICING_MODAL`,
  UnreadIndividualTut = `NOTIFICATIONS_INDIVIDUAL_UNREAD_TUT`,
  UnreadJourneyTut = `NOTIFICATIONS_JOURNEY_UNREAD_TUT`,

  EnableOnboardingChecklist = `ENABLE_ONBOARDING_CHECKLIST`,
  DismissedOnboardingChecklist = `DISMISSED_ONBOARDING_CHECKLIST`,

  DismissedStarterBoardModal = `DISMISSED_STARTER_BOARD_MODAL`,
  ViewedStarterBoardWithDataCount = `VIEWED_STARTER_BOARD_WITH_DATA_COUNT`,
  HideStarterBoardSurvey = `HIDE_STARTER_BOARD_SURVEY`,

  ViewedBenchmarkAnnouncementCount = `VIEWED_BENCHMARK_ANNOUNCEMENT_COUNT`,
  DisableBenchmarkAnnouncement = `DISABLE_BENCHMARK_ANNOUNCEMENT`,

  OnboardingQuestionnaireShownCount = `ONBOARDING_QUESTIONNAIRE_SHOWN_COUNT`,

  DismissedLexiconCohortsMovedModal = `DISMISSED_LEXICON_COHORTS_MOVED_MODAL`,
}

export enum FeatureGate {
  AllowCustomAlerts = `allow_custom_alerts`,
  AllowFormattedExport = `allow_formatted_export`,
  AllowRawDataExport = `allow_raw_data_export`,
  AllowSmartHub = `allow_smart_hub`,
  AnomalyDetection = `anomaly_detection`,
  B2BAnalytics = `b2b_analytics`,
  B2BAnalyticsGroups = `b2b_analytics_groups`,
  CanCustomizeCsvLimit = `can_customize_csv_limit`,
  CanDeleteExperiments = `can_delete_experiments`,
  CanDropEventsAndProperties = `can_drop_events_and_properties`,
  CanExportCsv = `can_export_csv`,
  CanHideEventsAndProperties = `can_hide_events_and_properties`,
  CanMergeEventsAndProperties = `can_merge_events_and_properties`,
  CanSegmentRetention = `can_segment_retention`,
  CanUseDatawarehouseExport = `can_use_datawarehouse_export`,
  CanUsePlatformGithub = `can_use_platform_github`,
  DataClassification = `data_classification`,
  // event_approval feature gate is superseded by data_governance
  DataGovernance = `data_governance`,
  EventApproval = `event_approval`,
  EventOwners = `event_owners`,
  ExperimentsApp = `experiments_app`,
  ExperimentsBreakdown = `experiments_breakdown`,
  ExperimentsStartDate = `experiments_start_date`,
  FeatureFlagsApp = `feature_flags_app`,
  IndiaDatacenterAccess = `india_datacenter_access`,
  MaxCustomAlerts = `max_custom_alerts`,
  MaxCustomApps = `max_custom_apps`,
  MaxCustomEvents = `max_custom_events`,
  MaxDashboardDigests = `max_dashboard_digests`,
  MaxDataHistoryDays = `max_data_history_days`,
  MaxExperiments = `max_experiments`,
  MaxInsightsFilters = `max_insights_filters`,
  MaxOrgMembers = `max_org_members`,
  MaxProjectMembers = `max_project_members`,
  MaxProjects = `max_projects`,
  MaxSavedReports = `max_saved_reports`,
  MaxSegmentationFilters = `max_segmentation_filters`,
  MetricTree = `metric_tree`,
  OnDemandAnalysis = `on_demand_analysis`,
  RestrictedReports = `restricted_reports`,
  'RestrictEntitySaving' = `restrict_entity_saving`,
  RootCauseAnalysis = `root_cause_analysis`,
  SessionReplay = `session_replay`,
  Sharing = `sharing`,
  SSO = `sso`,
  Workspaces = `workspaces`,
  WarehouseConnectors = `warehouse_connectors`,
  VerifiedContent = `verified_content`,
  DataStandards = `data_standards`,
}

export enum FeatureGateTier {
  Legacy = `legacy`,
  Free = `free`,
  Startup = `startup`,
  Business = `business`,
  Enterprise = `enterprise`,
}

/**
 * Features enabled for a certain kind of feature gated plan
 * Corresponds to either:
 *   - hardcoded FEATURE_GATES in configurations/settings_global.py
 *   - buyable feature gates viewable at /admin/agreements/buyable/ in the django admin
 */
export interface FeatureGatesValue {
  [FeatureGate.AllowCustomAlerts]: boolean;
  [FeatureGate.AllowFormattedExport]: boolean;
  [FeatureGate.AllowRawDataExport]: boolean;
  [FeatureGate.AllowSmartHub]: boolean;
  [FeatureGate.AnomalyDetection]: boolean;
  [FeatureGate.B2BAnalytics]: boolean;
  [FeatureGate.B2BAnalyticsGroups]: number;
  [FeatureGate.CanCustomizeCsvLimit]: boolean;
  [FeatureGate.CanDeleteExperiments]: boolean;
  [FeatureGate.CanDropEventsAndProperties]: boolean;
  [FeatureGate.CanExportCsv]: boolean;
  [FeatureGate.CanHideEventsAndProperties]: boolean;
  [FeatureGate.CanMergeEventsAndProperties]: boolean;
  [FeatureGate.CanSegmentRetention]: boolean;
  [FeatureGate.CanUseDatawarehouseExport]: boolean;
  [FeatureGate.CanUsePlatformGithub]: boolean;
  [FeatureGate.DataClassification]: boolean;
  [FeatureGate.DataGovernance]: boolean;
  [FeatureGate.EventApproval]: boolean;
  [FeatureGate.EventOwners]: boolean;
  [FeatureGate.ExperimentsApp]: boolean;
  [FeatureGate.ExperimentsBreakdown]: boolean;
  [FeatureGate.ExperimentsStartDate]: string | null;
  [FeatureGate.FeatureFlagsApp]: boolean;
  [FeatureGate.IndiaDatacenterAccess]: boolean;
  [FeatureGate.MaxCustomApps]: number;
  [FeatureGate.MaxCustomAlerts]: number;
  [FeatureGate.MaxCustomEvents]: number;
  [FeatureGate.MaxDashboardDigests]: number;
  [FeatureGate.MaxDataHistoryDays]: number;
  [FeatureGate.MaxExperiments]: number;
  [FeatureGate.MaxInsightsFilters]: number;
  [FeatureGate.MaxOrgMembers]: number;
  [FeatureGate.MaxProjectMembers]: number;
  [FeatureGate.MaxProjects]: number;
  [FeatureGate.MaxSavedReports]: number;
  [FeatureGate.MaxSegmentationFilters]: number;
  [FeatureGate.MetricTree]: boolean;
  [FeatureGate.OnDemandAnalysis]: boolean;
  [FeatureGate.RestrictEntitySaving]: string[];
  [FeatureGate.RestrictedReports]: string[];
  [FeatureGate.RootCauseAnalysis]: boolean;
  [FeatureGate.SessionReplay]: boolean;
  [FeatureGate.Sharing]: boolean;
  [FeatureGate.SSO]: boolean;
  [FeatureGate.Workspaces]: boolean;
  [FeatureGate.WarehouseConnectors]: {
    max_number_of_sources: number;
    max_number_of_syncs: number;
  };
  [FeatureGate.VerifiedContent]: boolean;
  [FeatureGate.DataStandards]: boolean;
}

/**
 * These correspond to the names in user/permission/config.json
 */
export enum Permission {
  AccessDashboardEmailDigests = `access_dashboard_email_digests`,
  AccessProjectSettings = `access_project_settings`,
  AddMemberToTeam = `add_member_to_team`,
  AddProjectToTeam = `add_project_to_team`,
  AddUser = `add_user`,
  AddUserToProject = `add_user_to_project`,
  ChangeNotificationApiKeys = `change_notification_api_keys`,
  ChangePredictEvent = `change_predict_event`,
  CreateProject = `create_project`,
  CreateServiceAccount = `create_service_account`,
  CreateTeam = `create_team`,
  DataDeletion = `data_deletion`,
  DataRetrieval = `data_retrieval`,
  DeleteLookupTable = `delete_lookup_table`,
  DeleteMembers = `delete_members`,
  DeleteOrg = `delete_org`,
  DeleteProfileFromExplore = `delete_profile_explore`,
  DeleteProject = `delete_project`,
  DeleteProjectFromTeam = `delete_project_from_team`,
  DeleteServiceAccount = `delete_service_account`,
  DeleteTeam = `delete_team`,
  DeleteUser = `delete_user`,
  DeleteUserFromTeam = `delete_user_from_team`,
  DownloadCSVData = `download_csv_data`,
  DropEventsProperties = `drop_events_properties`,
  EditABTesting = `edit_ab_testing`,
  EditCreatedEntityVisibility = `edit_created_entity_visibility`,
  EditCrossProjectBoardMoving = `edit_cross_project_board_moving`,
  EditEntityCreatedByOthers = `edit_entity_created_by_others`,
  EditEntityVisibility = `edit_entity_visibility`,
  EditIntegrations = `edit_integrations`,
  EditLockedCreatedEntity = `edit_locked_created_entity`,
  EditLockedEntity = `edit_locked_entity`,
  EditNotifications = `edit_notifications`,
  EditProjectAutotrack = `edit_project_autotrack`,
  EditProjectInfo = `edit_project_info`,
  EditProjectSecurity = `edit_project_security`,
  EditShareSettings = `edit_share_settings`,
  EditSurveys = `edit_surveys`,
  EditUserRoles = `edit_userroles`,
  EventDeletion = `event_deletion`,
  ExportCohorts = `integrations_export_cohorts`,
  HideEventsProperties = `hide_events_properties`,
  InviteAdmin = `invite_admin`,
  InviteAnalyst = `invite_analyst`,
  InviteConsumer = `invite_consumer`,
  LockCreatedEntity = `lock_created_entity`,
  LockEntity = `lock_entity`,
  ManageSensitiveData = `manage_sensitive_data`,
  ManageWorkspaces = `manage_workspaces`,
  MergeEventsProperties = `merge_events_properties`,
  ModifyGuestAccess = `modify_guest_access`,
  PinDashboards = `pin_dashboards`,
  RequestGdprData = `request_gdpr_data`,
  ResetApiKey = `reset_api_key`,
  ResetProject = `reset_project`,
  TransferProject = `transfer_project`,
  UpdateOrg = `update_org`,
  UpdateOrgBilling = `update_org_billing`,
  UpdateTeam = `update_team`,
  UpdateUser = `update_user`,
  ViewABTesting = `view_ab_testing`,
  ViewBaseReports = `view_base_reports`,
  ViewFindYourOrg = `manage_discoverability`,
  ViewNotifications = `view_notifications`,
  ViewOrganizationSettings = `view_organization_settings`,
  ViewOrgDataPrivacy = `view_org_data_privacy`,
  ViewOrgProjects = `view_org_projects`,
  ViewOrgTeams = `view_org_teams`,
  ViewOrgUsers = `view_org_users`,
  ViewProjectSecret = `view_project_secret`,
  ViewUsageAnalytics = `view_usage_analytics`,
  ViewAuditLogs = `view_audit_logs`,
  WriteAnnotations = `write_annotations`,
  WriteAutotrack = `write_autotrack`,
  WriteBehaviors = `write_behaviors`,
  WriteBookmarks = `write_bookmarks`,
  WriteCodeless = `write_codeless`,
  WriteCohorts = `write_cohorts`,
  WriteCustomAlerts = `write_custom_alerts`,
  WriteCustomEvents = `write_customevents`,
  WriteCustomProperties = `write_custom_properties`,
  WriteDashboards = `write_dashboards`,
  WriteDataDefinitions = `write_data_definitions`,
  WriteDataDefinitionSchema = `write_data_definition_schema`,
  WriteExperiments = `write_experiments`,
  WriteFeatureFlags = `write_feature_flags`,
  WriteHeatMaps = `write_heat_maps`,
  WriteLookupTables = `write_lookup_tables`,
  WriteLookupTablesMapping = `write_lookup_tables_mapping`,
  WriteMetrics = `write_metrics`,
  WriteMetricTrees = `write_metric_trees`,
  WriteProfileExplore = `write_profile_explore`,
  WriteThemes = `write_themes`,
  WriteTypedBookmarks = `write_typed_bookmarks`,
  WriteWarehouseSources = `write_warehouse_sources`,
  WritePlaylists = `write_playlists`,
}

/**
 * For non-PII use, add to STORAGE_ALLOWLIST in iron/common/gdpr-constants.ts
 */
export enum LocalStorageKey {
  SearchedTextPersistedState = `$mp_browse_search_text`,
  // Used to persist a partial search state in the full page "Discover" UI
  BrowsePersistedState = `$mp_browse_persisted_state`,
  // Used to store information about boards moved between projects.
  CrossProject = `$mp_cross_project`,
  // used by featuresStore to cache api response
  FeaturesCache = `$mp_features_cache`,
  // for creating messages from explore
  MessageTargetingFromExplore = `$mp_message_targeting_from_explore`,
  // used by Dashboards to remember the user's last opened dashboard ID for any project or workspace
  LastOpenedDashboard = `$mp_last_opened_dashboard`,
  // The last user project (i.e. excluding demo projects) that a user visited.
  LastUserProject = `$mp_last_user_project`,
  // list of the last 10 dashboards the user viewed. Used to show the "Recent" sidenav section in a A/B test. If the section persists, this key could be merged with "LastOpenedDashboard".
  RecentlyViewedDashboards = `$mp_recently_viewed_dashboards`,
  // Partial of mp-sidenav state to persist display settings. Specifically, this is used for the collapsed/expanded
  // state of dashboard sidenav sections (e.g. pinned, favorites)
  Sidenav = `$mp_sidenav`,
  GlobalSidenav = `$mp_global_sidenav`,
  // list of recently pinned dashboards ids that the user already saw. It is used to know if the "New" tag should be displayed
  SeenPinnedDashboardIds = `$mp_seen_pinned_dashboard_ids`,
  // list of recently pinned canvas ids that the user already saw. It is used to know if the "New" tag should be displayed
  SeenPinnedCanvasIds = `$mp_seen_pinned_canvas_ids`,
  // list of recently pinned content ids that the user already saw. It is used to know if the "New" tag should be displayed
  SeenPinnedContentIds = `$mp_seen_pinned_content_ids`,
  // dark mode
  DarkMode = `$mp_dark_mode`,
  // reports panels
  Panel = `$mp_panel`,
  // for persisting latest state in live view
  LastEventInspectorState = `$mp_last_event_inspector_state`,
  // for persisting the type of events/properties menu you are using
  QueryBlockMenuVersion = `$mp_query_block_menu_version`,
  // flag if user has seen the banner for new metric charts
  SeenNewMetricBanner = `$mp_seen_new_metric_banner`,
  // last properties that saved on create/update profile modal
  LastSavedProfileProperties = `$mp_last_saved_properties`,
  // show global filters on dashboards
  ShowDashboardsGlobalFilters = `$mp_dashboards_show_global_filters`,
  // used by currentProjectClient to cache API response
  CurrentProjectClientCache = `$mp_current_project_client_cache`,
  // used by dismiss tooltip tour to store which tour should not displayed again
  DismissTooltipTour = `$mp_dismiss_tooltip_tour`,
  // stores state for Onboarding Checklist
  OnboardingChecklist = `$mp_onboarding_checklist`,
  // list of seen foundational template board ids. Used to know if we should show the feedback toast.
  FoundationalTemplates = `$mp_foundational_templates`,
  // stores project_id -> organization_id mapping
  OrganizationId = `$mp_org`,
  EventsMenu = `$mp_events_menu`,
  // list of replay ids seen by user
  SeenReplayIds = `$mp_seen_replay_ids`,
  // stores Lexicon column selection information
  LexiconColumnSelection = `$mp_lexicon_column_selection`,
  // [METRICSGA] Delete when First Class Metrics GAs
  MetricsBetaButton = `$mp_metrics_beta_button`,
  // stores ReactTable column selection information
  ReactTableSelection = `$mp_react_table_selection`,
  // stores whether a user has seen the current homepage
  HomepageDashboardId = `$mp_homepage_dashboard`,
  // Stores whether the content sidenav is collapsed or expanded
  ContentSidenavCollapsed = `$mp_content_sidenav_collapsed`,
  // Stores whether the sendbird chat is opened or closed
  SendbirdHelpChatOpened = `$mp_sendbird_help_chat_opened`,
  // Stores details about session replay playlists (e.g. viewed replay IDs per playlist)
  SessionReplayPlaylists = `$mp_session_replay_playlists`,
}

/**
 * Used to delete any user-dependent localStorage data on logout and login.
 * Currently all of the keys in the enum above are user-specific and should be purged,
 * but if we want something to persist across auth and still use the common enum (we should) this should be changed into a subset.
 */
export const USER_DEPENDENT_LOCAL_STORAGE_KEYS: LocalStorageKey[] = Object.values(LocalStorageKey);

/**
 * Help links for basic report introductions
 */
export const REPORT_BASICS_LINKS: Partial<{[key in ReportName]: string}> = {
  [ReportName.Insights]: `https://docs.mixpanel.com/docs/analysis/reports/insights`,
  [ReportName.Funnels]: `https://docs.mixpanel.com/docs/analysis/reports/funnels`,
  [ReportName.Flows]: `https://docs.mixpanel.com/docs/analysis/reports/flows`,
  [ReportName.Retention]: `https://docs.mixpanel.com/docs/analysis/reports/retention`,
  [ReportName.Dashboards]: `https://docs.mixpanel.com/docs/analysis/boards`,
  [ReportName.Impact]: `https://docs.mixpanel.com/docs/analysis/advanced/impact`,
  [ReportName.ExperimentReporting]: `https://docs.mixpanel.com/docs/analysis/advanced/experiments`,
  [ReportName.Signal]: `https://docs.mixpanel.com/docs/analysis/advanced/signal`,
};

export const REPORTS_TO_ADD_DROPDOWN = [
  {
    label: `Insights Report`,
    icon: `brand-insights`,
    value: `insights`,
    href: `insights`,
  },
  {
    label: `Funnels Report`,
    icon: `brand-funnels`,
    value: `funnels`,
    href: `funnels`,
  },
  {
    label: `Flows Report`,
    icon: `brand-flows`,
    value: `flows`,
    href: `flows`,
  },
  {
    label: `Retention Report`,
    icon: `brand-retention`,
    value: `retention`,
    href: `retention`,
  },
];

export const INSIGHTS_LINE_CHART_TYPES = [ChartType.Line, ChartType.StackedLine];
export const INSIGHTS_BAR_CHART_TYPES = [ChartType.Bar, ChartType.StackedBar];

export const INSIGHTS_AGGREGATE_CSV_EXPORT_CHOICES = [10000, 20000, 30000, 40000, 50000];
export const CSV_EXPORT_CHOICES_PROJECT_OVERRIDES: Record<number, number[]> = {
  // Snapdeal
  3241052: [...INSIGHTS_AGGREGATE_CSV_EXPORT_CHOICES, 100000],
  // Global-e
  3137995: [...INSIGHTS_AGGREGATE_CSV_EXPORT_CHOICES, 100000],
  // Borderfree
  3373276: [...INSIGHTS_AGGREGATE_CSV_EXPORT_CHOICES, 100000],
  // Sri Mandir - Production
  2611185: [...INSIGHTS_AGGREGATE_CSV_EXPORT_CHOICES, 100000],
};
export const INSIGHTS_LINE_CSV_EXPORT_LIMIT = 10000;

export const REPORT_LIMITS = {
  [ReportName.Funnels]: {
    csvExport: 10000, // ten thousand
    query: 200,
    segments: {
      [ChartType.FunnelSteps]: {default: 6, max: 12},
      [ChartType.FunnelStepsMetric]: {default: 1, max: 12},
      [ChartType.FunnelTrend]: {default: 6, max: 12},
      [ChartType.FunnelFrequencyLine]: {default: 6, max: 12},
      [ChartType.FunnelFrequencyBar]: {default: 6, max: 12},
      [ChartType.FunnelTTCLine]: {default: 6, max: 12},
      [ChartType.FunnelTTCBar]: {default: 6, max: 12},
      [ChartType.FunnelMedianTTC]: {default: 1, max: 12},
    },
  },
  [ReportName.Insights]: {
    csvExport: 10000, // ten thousand
    query: 3000,
    segments: {
      [ChartType.Column]: {default: 6, max: 12},
      [ChartType.Line]: {default: 12, max: 50},
      [ChartType.Pie]: {default: 12, max: 50},
      [ChartType.Bar]: {default: 12, max: 300},
      [ChartType.Metric]: {default: 6, max: 12},
    },
  },
  [ReportName.Retention]: {
    csvExport: 10000, // ten thousand
    query: 200,
    segments: {
      [ChartType.RetentionLine]: {default: 6, max: 12},
      [ChartType.RetentionTrend]: {default: 6, max: 12},
      [ChartType.RetentionTrendMetric]: {default: 1, max: 12},
      [ChartType.RetentionTable]: {default: 6, max: 12},
      [ChartType.Frequency]: {default: 6, max: 12},
    },
  },
};

export const RESULTS_OVERALL_SUM_KEY = `$overall`;
export const RESULTS_OTHER_SUM_KEY = `$other`;
export const RESULTS_DID_NOT_CONVERT_KEY = `Did not convert`;

export const CONVERSION_WINDOW_UNITS: ConversionWindowUnit[] = [
  ConversionWindowUnit.Second,
  ConversionWindowUnit.Minute,
  ConversionWindowUnit.Hour,
  ConversionWindowUnit.Day,
  ConversionWindowUnit.Week,
  ConversionWindowUnit.Month,
  ConversionWindowUnit.Session,
];
export const DURATION_UNITS: string[] = [`second`, `minute`, `hour`, `day`, `week`, `month`];

export const MAX_HOLDING_PROPERTY_CONSTANT = 3;

export const CONVERSION_WINDOW_SIZE_LIMITS: Record<
  ConversionWindowUnit,
  {
    MIN: number;
    MAX: number;
  }
> = {
  second: {
    MIN: 2,
    MAX: 60 * 60 * 24 * 367,
  },
  minute: {
    MIN: 1,
    MAX: 60 * 24 * 367,
  },
  hour: {
    MIN: 1,
    MAX: 24 * 367,
  },
  day: {
    MIN: 1,
    MAX: 367,
  },
  week: {
    MIN: 1,
    MAX: 52,
  },
  month: {
    MIN: 1,
    MAX: 12, // each month is counted as 30 days
  },
  // Sessions are blocked to 12 at most, since the dqs approx pulls a month of data for each session conv. window.
  // This is an approximation since a user may not have any activity for a year followed by 10 sessions in one day.
  session: {
    MIN: 1,
    MAX: 12,
  },
};

export const HIGHCHARTS_LINKED_ID_SUFFIX = `-$mp-linked`;

export const ANOMALY_MARKERS_ID_SUFFIX = `-$mp-anomalies`;

export enum TimeBucketType {
  NORMAL = 0,
  LOWER = 1,
  HIGHER = 2,
}

/**
 * These correspond to OnboardingChecklistFlags.FLAGS in project/models.py
 */
export enum OnboardingChecklistFlag {
  HasViewedEvents = `has_viewed_events`,
}

// Should match $z-base
export const Z_INDEX_BASE = 1000;
// Should match $z-tooltip
export const Z_TOOLTIP = 6000;

/** these match the `global_access_type` constants defined in webapp/app_api/constants.py (`OFF_ACCESS` etc.) */
export enum ProjectShareLevel {
  Off = `off`,
  Viewer = `viewer`,
  Editor = `editor`,
}

export const REPORT_PERSISTENCE_COOKIE = `mp_persistence`;

export const PERSISTED_QUERY_PARAMS_ALLOW_SET = new Set([
  `feature-flags`,
  `disable-feature-flags`,
  `assets_commit`,
  `mp_source`,
]);

export const CUSTOM_EVENT_TAG = `*customevent*`;

// blueprints
export const BLUEPRINT_DATA_GROUP_ID = `5333270628333146381`;
export const BLUEPRINT_PROJECT = `3105696`;
